import { Injectable, inject } from '@angular/core';
import { TsCurrencyPipe } from '@shared/pipes/ts-currency.pipe';
import { GridApi, GridOptions, IsGroupOpenByDefaultParams } from 'ag-grid-enterprise';
import { ColumnDef } from 'tsui/lib/ts-table/ts-table.model';
import { ColDef } from 'ag-grid-community';

@Injectable({
  providedIn: 'root',
})
export class AssetManagementGridService {
  private readonly currencyPipe: TsCurrencyPipe = inject(TsCurrencyPipe);

  constructor() {}

  static get commonAccountColumnDef(): ColumnDef {
    return {
      label: 'Account',
      name: 'name',
      type: 'text',
      tsTableOptions: {
        pinned: 'left',
        minWidth: 300,
      },
    };
  }

  static getDataPath(data: any) {
    return data?.path;
  }

  static flattenTreeAndAddPath<T extends { children?: T[]; name: string }>(
    tree: T[],
    groupBy: { key: string; value: any },
    path = [],
  ): (T & { path: string[] })[] {
    let flatTree = [];

    tree.forEach((node) => {
      const accountWithPath = { ...node, path: [...path, node.name] };

      if (node[groupBy.key] === groupBy.value && node.children) {
        flatTree.push(accountWithPath);
        flatTree = flatTree.concat(
          AssetManagementGridService.flattenTreeAndAddPath(node.children, groupBy, [...path, node.name]),
        );
      } else {
        flatTree.push(accountWithPath);
      }
    });

    return flatTree;
  }

  static getAutoGroupColDef(headerName: string, field: string): ColDef {
    return {
      headerName,
      field,
      resizable: false,
      flex: 4,
      pinned: 'left',
      minWidth: 500,
      suppressHeaderMenuButton: true,
      suppressHeaderFilterButton: true,
      suppressHeaderContextMenu: true,
      suppressMovable: true,
      sortable: false,
      cellRendererParams: {
        suppressCount: true,
      },
    };
  }

  static getTreeDataGridCommonColDef() {
    return {
      sortable: false,
      flex: 1,
      maxWidth: 800,
      minWidth: 150,
      resizable: false,
      suppressHeaderMenuButton: true,
      suppressHeaderFilterButton: true,
      suppressHeaderContextMenu: true,
      suppressMovable: true,
    };
  }

  static getTreeDataGridCommonOptions(): GridOptions {
    return {
      columnHoverHighlight: true,
      groupTotalRow: 'bottom',
      isGroupOpenByDefault: (params: IsGroupOpenByDefaultParams) => {
        return (params.rowNode.data as any).display === 'group';
      },
      getRowClass: (params) => {
        if (params.data.display === 'group') {
          return 'group-account';
        }
      },
    };
  }

  static autoSizeColumns(gridApi?: GridApi) {
    if (gridApi == null) return;

    let allColIds = gridApi.getColumns().map((col) => {
      return col.getColId();
    });

    gridApi.autoSizeColumns(allColIds);
  }

  static getNumericValue(rowValue: string | number): number {
    if (rowValue == null) return;

    if (typeof rowValue === 'number') return rowValue;

    const inputDataConvertedToNumber = +rowValue;

    if (isNaN(inputDataConvertedToNumber)) {
      console.warn('Invalid numeric value', rowValue);
      return null;
    }

    return inputDataConvertedToNumber;
  }

  formatCurrencyToAccounting(value: `${string}` | `-${string}` | number, wrapNegativeInParentheses = false): string {
    if (value == null) {
      return '-';
    }

    const formattedValue = this.currencyPipe.transform(value) ?? '';

    if (!formattedValue) {
      return '-';
    }

    return wrapNegativeInParentheses && formattedValue.startsWith('-')
      ? `(${formattedValue.slice(1)})`
      : formattedValue;
  }

  formatVariance(value: string | number): string {
    if (value == null) {
      return '';
    }

    const variance = Number(value);

    if (isNaN(variance)) {
      console.error('Invalid period to date variance', value);
      return '';
    }

    return variance.toFixed(2) + '%';
  }
}
